import React, { useState, useEffect } from "react";
import { useAppStore } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import { pickRepoFolder } from "../../lib/fs/repo";
import { findUserByOid, readSettings, saveSettings, resolveLlmConfigs } from "../../lib/fs/config";
import { checkMasterCvExists } from "../../lib/api/profile";
import {
  VACANCY_EXTRACTION_SYSTEM_PROMPT,
  VACANCY_ANALYSIS_SYSTEM_PROMPT,
  ENRICHMENT_SYSTEM_PROMPT,
  DOCUMENT_GENERATION_SYSTEM_PROMPT,
  CONSOLIDATION_SYSTEM_PROMPT,
} from "../../lib/ai/prompts";

export default function SettingsScreen() {
  const t = useT();
  
  // App store state
  const language = useAppStore((s) => s.language);
  const setLanguage = useAppStore((s) => s.setLanguage);
  const repoPath = useAppStore((s) => s.repoPath);
  const user = useAppStore((s) => s.user);
  const lensUser = useAppStore((s) => s.lensUser);
  const accessToken = useAppStore((s) => s.accessToken);
  const setAuth = useAppStore((s) => s.setAuth);
  const settings = useAppStore((s) => s.settings);
  const setSettings = useAppStore((s) => s.setSettings);
  const setActiveCandidateFolder = useAppStore((s) => s.setActiveCandidateFolder);
  const setHasMasterCv = useAppStore((s) => s.setHasMasterCv);

  // Admin shared settings states (initialized from store settings)
  const [apiKey, setApiKey] = useState(settings?.anthropic_api_key || "");
  const [model, setModel] = useState(settings?.anthropic_model || "gemini-2.5-flash");
  const [urgencyDays, setUrgencyDays] = useState<number>(settings?.urgency_threshold_days ?? 5);
  const [emailEnabled, setEmailEnabled] = useState<boolean>(settings?.notification_email_enabled ?? false);
  
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // LLM prompt overrides (admin only, stored in settings.json)
  const [promptExtracting, setPromptExtracting] = useState(
    settings?.prompts?.vacancy_extraction || VACANCY_EXTRACTION_SYSTEM_PROMPT,
  );
  const [promptAnalysis, setPromptAnalysis] = useState(
    settings?.prompts?.vacancy_analysis || VACANCY_ANALYSIS_SYSTEM_PROMPT,
  );
  const [promptEnrichment, setPromptEnrichment] = useState(
    settings?.prompts?.enrichment || ENRICHMENT_SYSTEM_PROMPT,
  );
  const [promptDocGen, setPromptDocGen] = useState(
    settings?.prompts?.document_generation || DOCUMENT_GENERATION_SYSTEM_PROMPT,
  );
  const [promptConsolidation, setPromptConsolidation] = useState(
    settings?.prompts?.consolidation || CONSOLIDATION_SYSTEM_PROMPT,
  );
  const [activePromptTab, setActivePromptTab] = useState<"extraction" | "analysis" | "enrichment" | "docgen" | "consolidation">("extraction");

  // Sync state if store settings change
  useEffect(() => {
    if (settings) {
      setApiKey(settings.anthropic_api_key || "");
      setModel(settings.anthropic_model || "gemini-2.5-flash");
      setUrgencyDays(settings.urgency_threshold_days ?? 5);
      setEmailEnabled(settings.notification_email_enabled ?? false);
      setPromptExtracting(settings.prompts?.vacancy_extraction || VACANCY_EXTRACTION_SYSTEM_PROMPT);
      setPromptAnalysis(settings.prompts?.vacancy_analysis || VACANCY_ANALYSIS_SYSTEM_PROMPT);
      setPromptEnrichment(settings.prompts?.enrichment || ENRICHMENT_SYSTEM_PROMPT);
      setPromptDocGen(settings.prompts?.document_generation || DOCUMENT_GENERATION_SYSTEM_PROMPT);
      setPromptConsolidation(settings.prompts?.consolidation || CONSOLIDATION_SYSTEM_PROMPT);
    }
  }, [settings]);

  // Handle repository path selection
  async function handleChangeFolder() {
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const picked = await pickRepoFolder();
      if (picked.status === "picked" && user?.oid) {
        const newPath = picked.path;
        
        // Verify current user exists in the new repo
        const newUserRecord = await findUserByOid(newPath, user.oid);
        if (!newUserRecord) {
          setErrorMsg(t("settings.user_not_found"));
          return;
        }

        // Update auth state in store
        setAuth("signed_in", {
          user,
          lensUser: newUserRecord,
          repoPath: newPath,
          accessToken,
        });

        // Read and load new settings
        const nextSettings = await readSettings(newPath);
        setSettings(nextSettings);

        // Reset active candidate
        setActiveCandidateFolder(newUserRecord.folder);
        const cvExists = await checkMasterCvExists(newPath, newUserRecord.folder);
        setHasMasterCv(cvExists);

        setSuccessMsg("Repository map succesvol gewijzigd!");
      } else if (picked.status === "invalid") {
        setErrorMsg("Deze map bevat geen geldige config/settings.json.");
      }
    } catch (err: any) {
      setErrorMsg(`Fout bij laden van nieuwe map: ${err?.message || err}`);
    }
  }

  // Handle saving global settings (admin only)
  async function handleSaveSettings(e: React.FormEvent) {
    e.preventDefault();
    if (!repoPath || !lensUser?.is_admin || !settings) return;

    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    const updatedSettings = {
      ...settings,
      anthropic_api_key: apiKey.trim(),
      anthropic_model: model,
      urgency_threshold_days: Number(urgencyDays) || 5,
      notification_email_enabled: emailEnabled,
      prompts: {
        vacancy_extraction: promptExtracting !== VACANCY_EXTRACTION_SYSTEM_PROMPT ? promptExtracting : null,
        vacancy_analysis: promptAnalysis !== VACANCY_ANALYSIS_SYSTEM_PROMPT ? promptAnalysis : null,
        enrichment: promptEnrichment !== ENRICHMENT_SYSTEM_PROMPT ? promptEnrichment : null,
        document_generation: promptDocGen !== DOCUMENT_GENERATION_SYSTEM_PROMPT ? promptDocGen : null,
        consolidation: promptConsolidation !== CONSOLIDATION_SYSTEM_PROMPT ? promptConsolidation : null,
      },
    };

    try {
      await saveSettings(repoPath, updatedSettings);
      setSettings(updatedSettings);
      setSuccessMsg(t("settings.saved_success"));
    } catch (err: any) {
      setErrorMsg(`Fout bij opslaan van instellingen: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8 space-y-8 animate-in fade-in duration-200">
      
      {/* Header */}
      <div className="border-b border-slate-100 pb-4">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          {t("settings.title")}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Beheer uw lokale voorkeuren en de instellingen van de gedeelde bibliotheek.
        </p>
      </div>

      {/* Success/Error Alerts */}
      {successMsg && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 animate-in slide-in-from-top-2 duration-150">
          <div className="flex gap-2.5">
            <span className="text-emerald-600 text-sm">✓</span>
            <p className="text-xs font-semibold text-emerald-800">{successMsg}</p>
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 animate-in slide-in-from-top-2 duration-150">
          <div className="flex gap-2.5">
            <span className="text-red-600 text-sm">⚠️</span>
            <p className="text-xs font-semibold text-red-800">{errorMsg}</p>
          </div>
        </div>
      )}

      {/* LLM Configurations — admin only, read-only */}
      {lensUser?.is_admin && settings && (
        <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/50 shadow-xs p-6">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-700 mb-5">
            <span className="text-base">⚙️</span>
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">LLM Configurations</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Active provider pool. Default on startup: <span className="font-semibold">{resolveLlmConfigs(settings).find((c) => c.id === settings.llm_default)?.label ?? settings.llm_default ?? resolveLlmConfigs(settings)[0]?.label}</span>.
                To add or modify configurations, edit <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded text-[9px]">config/settings.json</code> in the shared repo.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {resolveLlmConfigs(settings).map((cfg) => (
              <div key={cfg.id} className="flex items-center gap-3 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-700/30 px-4 py-3">
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${
                  cfg.provider === "gemini" ? "bg-blue-400" :
                  cfg.provider === "anthropic" ? "bg-violet-400" : "bg-emerald-400"
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{cfg.label}</p>
                  <p className="text-2xs text-slate-400 mt-0.5">
                    <span className="capitalize">{cfg.provider}</span>
                    <span className="mx-1">·</span>
                    <span className="font-mono">{cfg.model}</span>
                    {cfg.base_url && <><span className="mx-1">·</span><span className="font-mono">{cfg.base_url}</span></>}
                  </p>
                </div>
                {cfg.api_key && (
                  <span className="text-2xs font-mono text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                    {cfg.api_key.substring(0, 8)}••••••••
                  </span>
                )}
                {settings.llm_default === cfg.id && (
                  <span className="text-2xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">default</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LLM Prompt Configuration Card — full width, admin only */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-50 mb-6">
          <span className="text-base">🧠</span>
          <div>
            <h2 className="text-base font-bold text-slate-800">LLM Prompt Configuration</h2>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Override the built-in system prompts. Editing a prompt and saving stores it in settings.json — all Lens instances will use the override immediately. Clear a prompt to revert to the built-in default.
            </p>
          </div>
        </div>

        {!lensUser?.is_admin ? (
          <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 text-center space-y-2">
            <span className="text-base">🔒</span>
            <p className="text-xs font-semibold text-amber-800">Toegang Beperkt</p>
            <p className="text-[11px] text-amber-600 leading-relaxed">
              Prompt overrides kunnen alleen worden ingesteld door een beheerder.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Prompt tab selector */}
            <div className="bg-slate-100/70 p-1 rounded-xl flex gap-1 border border-slate-200/40">
              {(
                [
                  { id: "extraction", label: "Extraction" },
                  { id: "analysis", label: "Analysis" },
                  { id: "enrichment", label: "Enrichment" },
                  { id: "docgen", label: "Document Gen" },
                  { id: "consolidation", label: "Consolidation" },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActivePromptTab(tab.id)}
                  className={`flex-1 py-2 rounded-lg text-[10px] font-semibold tracking-wider transition-all cursor-pointer focus:outline-none uppercase ${
                    activePromptTab === tab.id
                      ? "bg-white text-slate-950 shadow-xs border border-slate-200/35"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Prompt textarea */}
            {activePromptTab === "extraction" && (
              <div className="space-y-2 animate-in fade-in duration-100">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Vacancy Extraction prompt
                  </label>
                  <button
                    type="button"
                    onClick={() => setPromptExtracting(VACANCY_EXTRACTION_SYSTEM_PROMPT)}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={promptExtracting}
                  onChange={(e) => setPromptExtracting(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="w-full text-[11px] font-mono rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700 shadow-2xs focus:border-indigo-400 focus:outline-none transition-colors resize-y leading-relaxed"
                />
                <p className="text-[9px] text-slate-400">{promptExtracting.length} chars</p>
              </div>
            )}

            {activePromptTab === "analysis" && (
              <div className="space-y-2 animate-in fade-in duration-100">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Vacancy Analysis prompt
                  </label>
                  <button
                    type="button"
                    onClick={() => setPromptAnalysis(VACANCY_ANALYSIS_SYSTEM_PROMPT)}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={promptAnalysis}
                  onChange={(e) => setPromptAnalysis(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="w-full text-[11px] font-mono rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700 shadow-2xs focus:border-indigo-400 focus:outline-none transition-colors resize-y leading-relaxed"
                />
                <p className="text-[9px] text-slate-400">{promptAnalysis.length} chars</p>
              </div>
            )}

            {activePromptTab === "enrichment" && (
              <div className="space-y-2 animate-in fade-in duration-100">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Enrichment prompt
                  </label>
                  <button
                    type="button"
                    onClick={() => setPromptEnrichment(ENRICHMENT_SYSTEM_PROMPT)}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={promptEnrichment}
                  onChange={(e) => setPromptEnrichment(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="w-full text-[11px] font-mono rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700 shadow-2xs focus:border-indigo-400 focus:outline-none transition-colors resize-y leading-relaxed"
                />
                <p className="text-[9px] text-slate-400">{promptEnrichment.length} chars</p>
              </div>
            )}

            {activePromptTab === "docgen" && (
              <div className="space-y-2 animate-in fade-in duration-100">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Document Generation prompt
                  </label>
                  <button
                    type="button"
                    onClick={() => setPromptDocGen(DOCUMENT_GENERATION_SYSTEM_PROMPT)}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={promptDocGen}
                  onChange={(e) => setPromptDocGen(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="w-full text-[11px] font-mono rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700 shadow-2xs focus:border-indigo-400 focus:outline-none transition-colors resize-y leading-relaxed"
                />
                <p className="text-[9px] text-slate-400">{promptDocGen.length} chars</p>
              </div>
            )}

            {activePromptTab === "consolidation" && (
              <div className="space-y-2 animate-in fade-in duration-100">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Consolidation prompt
                  </label>
                  <button
                    type="button"
                    onClick={() => setPromptConsolidation(CONSOLIDATION_SYSTEM_PROMPT)}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={promptConsolidation}
                  onChange={(e) => setPromptConsolidation(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="w-full text-[11px] font-mono rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700 shadow-2xs focus:border-indigo-400 focus:outline-none transition-colors resize-y leading-relaxed"
                />
                <p className="text-[9px] text-slate-400">{promptConsolidation.length} chars</p>
              </div>
            )}

            <p className="text-[10px] text-slate-400 pt-1">
              Prompts are saved via the <strong>Save Shared Settings</strong> button above. Changes take effect on the next AI call — no restart required.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        
        {/* Device Preferences Card */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 flex flex-col justify-between">
          <div className="space-y-6">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-50">
              <span className="text-base">💻</span>
              <h2 className="text-base font-bold text-slate-800">
                {t("settings.device_prefs")}
              </h2>
            </div>

            {/* Repository Sync Path */}
            <div className="form-group">
              <label className="form-label">{t("settings.repo_path")}</label>
              <div className="mt-1 bg-slate-50 border border-slate-100 rounded-xl p-3 space-y-2">
                <p className="font-mono text-[10px] text-slate-600 break-all select-all">
                  {repoPath || "Geen map geselecteerd"}
                </p>
                <button
                  type="button"
                  onClick={handleChangeFolder}
                  className="w-full flex justify-center items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3.5 py-2 text-xs font-semibold shadow-xs hover:shadow-sm transition-all cursor-pointer focus:outline-none"
                >
                  <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-1 11H5c-.55 0-1-.45-1-1V9c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v7c0 .55-.45 1-1 1z" />
                  </svg>
                  {t("settings.change_folder")}
                </button>
              </div>
            </div>

            {/* Language Selector */}
            <div className="form-group">
              <label className="form-label">{t("settings.lang")}</label>
              <div className="flex gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setLanguage("nl")}
                  className={`flex-1 flex justify-center items-center py-2.5 rounded-xl border text-xs font-semibold transition-all cursor-pointer focus:outline-none ${
                    language === "nl"
                      ? "bg-slate-900 border-slate-900 text-white shadow-xs"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  🇳🇱 Nederlands
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage("en")}
                  className={`flex-1 flex justify-center items-center py-2.5 rounded-xl border text-xs font-semibold transition-all cursor-pointer focus:outline-none ${
                    language === "en"
                      ? "bg-slate-900 border-slate-900 text-white shadow-xs"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  🇬🇧 English
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-4 border-t border-slate-50 text-[10px] text-slate-400 text-center">
            Lens App Versie {settings?.lens_version || "0.1.0"}
          </div>
        </div>

        {/* Shared Repository Configurations Card */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-50 mb-6">
            <span className="text-base">⚙️</span>
            <h2 className="text-base font-bold text-slate-800">
              {t("settings.shared_settings")}
            </h2>
          </div>

          {!lensUser?.is_admin ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 text-center space-y-2">
              <span className="text-base">🔒</span>
              <p className="text-xs font-semibold text-amber-800">
                Toegang Beperkt
              </p>
              <p className="text-[11px] text-amber-600 leading-relaxed">
                U bent ingelogd als reguliere gebruiker. Gedeelde bibliotheekinstellingen (zoals AI API-sleutels, modelkeuzes en meldingen) kunnen alleen worden gewijzigd door een beheerder.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSaveSettings} className="space-y-5">
              
              {/* API Key */}
              <div className="form-group">
                <label className="form-label">{t("settings.api_key")}</label>
                <div className="relative mt-1">
                  <input
                    type={showApiKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="AIzaSy..."
                    required
                    className="form-input pr-10 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer text-xs"
                  >
                    {showApiKey ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {/* Model */}
              <div className="form-group">
                <label className="form-label">{t("settings.model")}</label>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="form-select mt-1"
                >
                  <option value="gemini-2.5-flash">Gemini 2.5 Flash (Snelle extractie)</option>
                  <option value="gemini-2.5-pro">Gemini 2.5 Pro (Hogere nauwkeurigheid)</option>
                  <option value="claude-3-5-sonnet-latest">Claude 3.5 Sonnet (Alternatief)</option>
                </select>
              </div>

              {/* Urgency Threshold */}
              <div className="form-group">
                <label className="form-label">{t("settings.urgency")}</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={urgencyDays}
                  onChange={(e) => setUrgencyDays(Number(e.target.value))}
                  className="form-input mt-1"
                />
              </div>

              {/* Email Notifications toggle */}
              <div className="flex items-center justify-between bg-slate-50/50 border border-slate-100 p-3 rounded-xl">
                <div className="space-y-0.5 max-w-[80%]">
                  <label htmlFor="notifications-toggle" className="text-xs font-semibold text-slate-700 select-none cursor-pointer">
                    {t("settings.notifications")}
                  </label>
                  <p className="text-[10px] text-slate-400">
                    Stuur e-mails naar consultants als er nieuwe vacatures worden toegevoegd.
                  </p>
                </div>
                <input
                  id="notifications-toggle"
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                  className="h-4.5 w-4.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={saving}
                className="w-full mt-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white py-2.5 text-xs font-semibold shadow-xs hover:shadow-sm transition-all cursor-pointer focus:outline-none disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-wait"
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-white" />
                    Opslaan…
                  </span>
                ) : (
                  t("settings.save")
                )}
              </button>

            </form>
          )}
        </div>

      </div>

    </div>
  );
}
